import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';
import { shouldThrottle } from '@/util/rateLimit.js';
import posthog from '@/composables/usePostHog.js';
import EGS from '@/util/egs_util.js';
import { parseStoredJson, type ImpersonationState, type SessionUserInfo } from '@/util/authSession';
import type { ClientRecord } from '@/store/clientStore.ts';

interface GroupMembershipRecord extends Record<string, unknown> {
	roleName?: string | string[] | null;

	group?: {
		groupNumber?: number | null;
		name?: string | null;
		shortName?: string | null;
	} | null;
}

interface UserProfileRecord extends Record<string, unknown> {
	userNumber?: number | null;
}

interface UnknownRecord {
	[key: string]: unknown;
}

interface StoredLicenseRecord extends UnknownRecord {
	userNumber?: number | string | null;
}

interface StoredLicensesByUser {
	[userNumber: number]: StoredLicenseRecord[] | undefined;
}

interface StoredLicenseCacheByUser {
	[userNumber: number]: StoredLicenseCacheEntry | undefined;
}

interface StoredLicenseRequestsByUser {
	[userNumber: number]: Promise<StoredLicenseRecord[]> | undefined;
}

interface UsersState {
	users: UserProfileRecord[];
	isAuthenticated: boolean;
	authToken: string | null;
	userInfo: Record<string, unknown>;
	groups: GroupMembershipRecord[];
	roles: string[];
	userDTO: Record<string, unknown>;
	groupsLoaded: boolean;
	groupsLoadedForUserNumber: number | null;
	groupsFetchPromise: Promise<GroupMembershipRecord[]> | null;
	storedLicensesByUserNumber: StoredLicenseCacheByUser;
	storedLicenseRequests: StoredLicenseRequestsByUser;
	trendLine: Record<number, unknown> | null;
}

interface UserMutationResult {
	success: boolean;
	message?: string;
	data?: unknown;
	status?: number | undefined;
}

export type InviteGroupOwnerResult =
	| (UserProfileRecord & {
			success: true;
	  })
	| {
			success: false;
			message?: string;
			status?: number | undefined;
			error?: unknown;
	  };

interface ApiErrorPayload {
	message?: string;
	error?: string;
}

interface StoredLicenseCacheEntry {
	licenses: StoredLicenseRecord[];
	expiresAt: number;
}
export interface BulkUserUploadUser {
	firstName: string;
	middleName: string | null;
	lastName: string;
	gender: string | null;
	birthdate: string | null;
	email: string;
	primaryPhone: string | null;
}

export interface BulkUserUploadRequest {
	groupNumber: number;
	roles: string[];
	users: BulkUserUploadUser[];
}

export interface EmployeeUserSearchParams {
	page: number;
	size: number;
	includeDemoUsers: boolean;
	groupId?: number;
	userNumber?: number;
	search?: string;
	city?: string;
	state?: string;
}

export interface EmployeeUserPage {
	content: UserProfileRecord[];
	totalElements: number;
	totalPages?: number;
	size?: number;
	number?: number;
	first?: boolean;
	last?: boolean;
}

const STORED_LICENSE_CACHE_NAME = 'evergreen-users-license-cache-v1';

function extractRoles(groups: GroupMembershipRecord[] = []): string[] {
	return [
		...new Set(
			groups
				.flatMap((group) => {
					const roleNames = group.roleName;
					if (Array.isArray(roleNames)) {
						return roleNames;
					}
					if (typeof roleNames === 'string' && roleNames) {
						return [roleNames];
					}
					return [];
				})
				.filter(Boolean)
		)
	];
}

function getActiveSessionUserNumber(): number | null {
	const impersonationState = parseStoredJson<ImpersonationState | null>(sessionStorage.getItem('impersonation_state'), null);
	const impersonatedUserNumber = impersonationState?.isImpersonating && typeof impersonationState.impersonatedUser?.['userNumber'] === 'number' ? impersonationState.impersonatedUser['userNumber'] : null;
	if (impersonatedUserNumber != null) {
		return impersonatedUserNumber;
	}

	const sessionUser = parseStoredJson<SessionUserInfo | null>(sessionStorage.getItem('user_info'), null);
	return typeof sessionUser?.userNumber === 'number' ? sessionUser.userNumber : null;
}

function storedLicensesCacheKey(userNumber: number): string {
	const viewerNumber = getActiveSessionUserNumber() ?? 'anonymous';
	return new URL(`/__cache/users/licenses/${String(userNumber)}`, window.location.origin).toString() + `?viewer=${encodeURIComponent(String(viewerNumber))}`;
}

function getCachedStoredLicenses(entry: StoredLicenseCacheEntry | null | undefined): StoredLicenseRecord[] | null {
	if (!entry || (entry.expiresAt && Date.now() > entry.expiresAt)) return null;
	return Array.isArray(entry.licenses) ? entry.licenses : null;
}

function ttlUntilMidnight(): number {
	const now = new Date();
	const midnight = new Date(now);
	midnight.setHours(24, 0, 0, 0);
	return Math.max(0, midnight.getTime() - now.getTime());
}

async function readCachedStoredLicenses(userNumber: number): Promise<StoredLicenseCacheEntry | null> {
	if (typeof caches === 'undefined') return null;

	try {
		const cache = await caches.open(STORED_LICENSE_CACHE_NAME);
		const response = await cache.match(storedLicensesCacheKey(userNumber));
		if (!response) return null;
		const cached = (await response.json()) as StoredLicenseCacheEntry;
		return getCachedStoredLicenses(cached) ? cached : null;
	} catch {
		return null;
	}
}

async function cacheStoredLicenses(userNumber: number, entry: StoredLicenseCacheEntry): Promise<void> {
	if (typeof caches === 'undefined') return;

	try {
		const cache = await caches.open(STORED_LICENSE_CACHE_NAME);
		await cache.put(
			storedLicensesCacheKey(userNumber),
			new Response(JSON.stringify(entry), {
				headers: { 'Content-Type': 'application/json' }
			})
		);
	} catch {
		// Continue using the in-memory cache when Cache Storage is unavailable.
	}
}

function licenseUserNumber(license: unknown): number | null {
	if (!license || typeof license !== 'object') return null;
	const record = license as UnknownRecord;
	const user = record['user'];
	const nestedUser = user && typeof user === 'object' ? (user as UnknownRecord) : null;
	const candidate = record['userNumber'] ?? record['userId'] ?? nestedUser?.['userNumber'] ?? nestedUser?.['userId'];
	const userNumber = Number(candidate);
	return Number.isSafeInteger(userNumber) && userNumber > 0 ? userNumber : null;
}

function groupLicensesByUserNumber(licenses: StoredLicenseRecord[], userNumbers: number[]): StoredLicensesByUser {
	const grouped = Object.fromEntries(userNumbers.map((userNumber) => [userNumber, []])) as StoredLicensesByUser;
	const onlyUserNumber = userNumbers.length === 1 ? userNumbers[0] : undefined;
	for (const license of licenses) {
		const userNumber = licenseUserNumber(license);
		if (userNumber != null && grouped[userNumber]) {
			grouped[userNumber].push(license);
		} else if (onlyUserNumber != null) {
			const onlyUserLicenses = grouped[onlyUserNumber];
			if (onlyUserLicenses) onlyUserLicenses.push(license);
		}
	}
	return grouped;
}

export const useUsersStore = defineStore('usersStore', {
	state: (): UsersState => ({
		users: [],
		isAuthenticated: false,
		authToken: null,
		userInfo: {},
		groups: [],
		roles: [],
		userDTO: {},
		groupsLoaded: false,
		groupsLoadedForUserNumber: null,
		groupsFetchPromise: null,
		storedLicensesByUserNumber: {},
		storedLicenseRequests: {},
		trendLine: []
	}),
	getters: {
		hasRole:
			(state) =>
			(requiredRoles: string[] = []) =>
				Array.isArray(requiredRoles) && requiredRoles.length > 0 && requiredRoles.some((role) => state.roles.includes(role))
	},
	actions: {
		setGroups(groups: GroupMembershipRecord[] = [], userNumber: number | null = null) {
			this.groups = Array.isArray(groups) ? groups : [];
			this.roles = extractRoles(this.groups);
			this.groupsLoaded = true;
			this.groupsLoadedForUserNumber = userNumber;

			localStorage.setItem('user_groups', JSON.stringify(this.groups));
			localStorage.setItem('user_roles', JSON.stringify(this.roles));
			if (userNumber != null) {
				localStorage.setItem('user_groups_user_number', String(userNumber));
			} else {
				localStorage.removeItem('user_groups_user_number');
			}
		},
		resetGroupsState() {
			this.groups = [];
			this.roles = [];
			this.groupsLoaded = false;
			this.groupsLoadedForUserNumber = null;
			this.groupsFetchPromise = null;

			localStorage.removeItem('user_groups');
			localStorage.removeItem('user_roles');
			localStorage.removeItem('user_groups_user_number');
		},
		hydrateGroupsFromStorage() {
			const savedGroups = parseStoredJson<GroupMembershipRecord[]>(localStorage.getItem('user_groups'), []);
			const savedRoles = parseStoredJson<string[]>(localStorage.getItem('user_roles'), []);
			const savedUserNumber = localStorage.getItem('user_groups_user_number');

			this.groups = Array.isArray(savedGroups) ? savedGroups : [];
			this.roles = Array.isArray(savedRoles) ? savedRoles : extractRoles(this.groups);
			this.groupsLoaded = true;
			this.groupsLoadedForUserNumber = savedUserNumber != null ? Number(savedUserNumber) : null;
		},
		async fetchAllUsers() {
			if (shouldThrottle('fetchAllUsers', 100)) {
				posthog.capture('throttled_action', { action: 'fetchAllUsers' });
				return;
			}

			const response = await apiClient.get<UserProfileRecord[]>('/users/all');
			this.users = response.data;
			return this.users;
		},
		async fetchEmployeeUsers(params: EmployeeUserSearchParams): Promise<EmployeeUserPage> {
			if (shouldThrottle('fetchEmployeeUsers', 100)) {
				posthog.capture('throttled_action', {
					action: 'fetchEmployeeUsers'
				});

				return {
					content: [],
					totalElements: 0
				};
			}

			const response = await apiClient.get<EmployeeUserPage>('/employee/users', {
				params
			});

			return response.data;
		},
		async fetchTrendLine() {
			if (shouldThrottle('fetchTrendLine', 100)) {
				posthog.capture('throttled_action', { action: 'fetchTrendLine' });
				return;
			}
			const response = await apiClient.get<UserProfileRecord>('/employee/users/trendLine');
			this.trendLine = response.data;
			return this.trendLine;
		},
		async fetchByUserNumber(userNumber: number) {
			if (shouldThrottle(`fetchByUserNumber:${String(userNumber)}`, 100)) {
				posthog.capture('throttled_action', { action: 'fetchByUserNumber' });
				return;
			}

			const response = await apiClient.get<UserProfileRecord>(`/users/id/${userNumber}`);
			return response.data;
		},
		async fetchStoredLicenses(userNumber: number, force: boolean = false): Promise<StoredLicenseRecord[]> {
			if (this.storedLicenseRequests[userNumber]) {
				return this.storedLicenseRequests[userNumber];
			}

			const request = this.fetchStoredLicensesForUsers([userNumber], force).then((licensesByUserNumber) => licensesByUserNumber[userNumber] ?? []);
			this.storedLicenseRequests[userNumber] = request;

			try {
				return await request;
			} finally {
				this.storedLicenseRequests[userNumber] = undefined;
			}
		},
		async fetchStoredLicensesForUsers(userNumbers: number[], force: boolean = false): Promise<StoredLicensesByUser> {
			const uniqueUserNumbers = [...new Set(userNumbers.map(Number).filter((userNumber) => Number.isSafeInteger(userNumber) && userNumber > 0))];
			const licensesByUserNumber = Object.fromEntries(uniqueUserNumbers.map((userNumber) => [userNumber, []])) as StoredLicensesByUser;
			const missingUserNumbers: number[] = [];

			for (const userNumber of uniqueUserNumbers) {
				const cachedInMemory = getCachedStoredLicenses(this.storedLicensesByUserNumber[userNumber]);
				if (!force && cachedInMemory) {
					licensesByUserNumber[userNumber] = cachedInMemory;
					continue;
				}

				if (!force) {
					const cachedEntry = await readCachedStoredLicenses(userNumber);
					const cachedLicenses = getCachedStoredLicenses(cachedEntry);
					if (cachedLicenses && cachedEntry) {
						this.storedLicensesByUserNumber[userNumber] = cachedEntry;
						licensesByUserNumber[userNumber] = cachedLicenses;
						continue;
					}
				}

				missingUserNumbers.push(userNumber);
			}

			if (!missingUserNumbers.length) return licensesByUserNumber;

			const { data } = await apiClient.post<StoredLicenseRecord[]>('/users/licenses/query', { userNumbers: missingUserNumbers });
			const fetchedLicensesByUserNumber = groupLicensesByUserNumber(Array.isArray(data) ? data : [], missingUserNumbers);
			await Promise.all(
				missingUserNumbers.map(async (userNumber) => {
					const licenses = fetchedLicensesByUserNumber[userNumber] ?? [];
					const entry = { licenses, expiresAt: Date.now() + ttlUntilMidnight() } satisfies StoredLicenseCacheEntry;
					this.storedLicensesByUserNumber[userNumber] = entry;
					licensesByUserNumber[userNumber] = licenses;
					await cacheStoredLicenses(userNumber, entry);
				})
			);

			return licensesByUserNumber;
		},
		async usersGroups(options: { force?: boolean } = {}): Promise<GroupMembershipRecord[]> {
			const { force = false } = options;
			const currentUserNumber = getActiveSessionUserNumber();

			if (this.groupsFetchPromise) {
				return this.groupsFetchPromise;
			}

			if (!force && this.groupsLoaded && this.groupsLoadedForUserNumber === currentUserNumber) {
				return this.groups;
			}

			this.groupsFetchPromise = (async () => {
				const { data } = await apiClient.get<GroupMembershipRecord[]>('/users/currentUser/groupRoles');
				this.setGroups(data, currentUserNumber);
				return this.groups;
			})();

			try {
				return await this.groupsFetchPromise;
			} finally {
				this.groupsFetchPromise = null;
			}
		},
		async userAssignor(): Promise<ClientRecord[] | undefined> {
			if (shouldThrottle('fetchUserAssignor', 100)) {
				posthog.capture('throttled_action', { action: 'fetchUserAssignor' });
				return undefined;
			}

			const response = await apiClient.get<ClientRecord[]>('/users/currentUser/assignor');
			return response.data;
		},
		async addUser(user: Record<string, unknown>): Promise<UserMutationResult> {
			if (shouldThrottle('addUser', 100)) {
				posthog.capture('throttled_action', { action: 'addUser' });
				return { success: false };
			}

			try {
				const response = await apiClient.post('/auth/register', user);

				if (response.status === 201 || response.status === 200) {
					return {
						success: true,
						data: response.data,
						status: response.status
					};
				}

				posthog.capture('registration_unexpected_status', { status: response.status });
				return { success: false, status: response.status, message: 'Registration Failed' };
			} catch (error: unknown) {
				const responseStatus = typeof error === 'object' && error && 'response' in error ? (error as { response?: { status?: number } }).response?.status : undefined;

				const responseMessage = typeof error === 'object' && error && 'response' in error ? ((error as { response?: { data?: string } }).response?.data ?? 'Registration Failed') : 'Registration Failed';

				return { success: false, status: responseStatus, message: responseMessage };
			}
		},
		async inviteGroupOwner(payload: { email: string; firstName: string; lastName: string; groupName: string | null | undefined }): Promise<InviteGroupOwnerResult> {
			if (shouldThrottle('inviteGroupOwner', 100)) {
				posthog.capture('throttled_action', { action: 'inviteGroupOwner' });
				return { success: false, message: 'Request throttled' };
			}

			try {
				const response = await apiClient.post<UserProfileRecord | null>('/users/invite/group-owner', payload);
				return response.data ? { ...response.data, success: true } : { success: false, message: 'Invitation failed' };
			} catch (error: unknown) {
				posthog.capture('inviteGroupOwner_error', { error: error instanceof Error ? error.message : String(error) });
				const responseStatus = typeof error === 'object' && error && 'response' in error ? (error as { response?: { status?: number } }).response?.status : undefined;
				const responseData = typeof error === 'object' && error && 'response' in error ? (error as { response?: { data?: string | ApiErrorPayload } }).response?.data : undefined;
				const responseMessage = typeof responseData === 'string' ? responseData : (responseData?.message ?? responseData?.error ?? 'Invitation failed');
				return { success: false, status: responseStatus, message: responseMessage, error };
			}
		},
		async fetchCurrentUserProfile(): Promise<Record<string, unknown> | undefined> {
			if (shouldThrottle('fetchCurrentUserProfile', 100)) {
				posthog.capture('throttled_action', { action: 'fetchCurrentUserProfile' });
				return undefined;
			}

			const response = await apiClient.get<Record<string, unknown>>('/users/currentUser');
			return response.data;
		},
		async updateUser(userNumber: number, patch: Record<string, unknown>): Promise<UserMutationResult> {
			if (shouldThrottle('updateUser', 100)) {
				posthog.capture('throttled_action', { action: 'updateUser' });
				return { success: false, message: 'Request throttled' };
			}

			try {
				const response = await apiClient.patch<Parameters<typeof EGS.UserInformation.syncSessionUserInfo>[0]>(`/users/edit/${userNumber}`, patch, {
					headers: {
						'Content-Type': 'application/merge-patch+json'
					}
				});
				if (response.status === 200) {
					EGS.UserInformation.syncSessionUserInfo(response.data);
					return { success: true, data: response.data };
				}
				return { success: false, message: 'Failed to update user' };
			} catch (error: unknown) {
				const message = typeof error === 'object' && error && 'response' in error ? ((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Update failed') : 'Update failed';
				return { success: false, message };
			}
		},
		async uploadUsers(payload: BulkUserUploadRequest) {
			if (shouldThrottle('uploadUsers', 1000)) {
				posthog.capture('throttled_action', {
					action: 'uploadUsers'
				});

				throw new Error('The user upload request was submitted too quickly.');
			}

			if (!Array.isArray(payload.users) || payload.users.length === 0) {
				throw new Error('At least one user is required for a bulk upload.');
			}

			if (!Number.isInteger(payload.groupNumber) || payload.groupNumber <= 0) {
				throw new Error('A valid group number is required.');
			}

			if (!Array.isArray(payload.roles) || payload.roles.length === 0) {
				throw new Error('At least one role is required.');
			}

			return apiClient.post('/users/saveAll', payload);
		}
	}
});
